import { useCallback } from 'react';
import useApi from './useApi';
import { useAppSettings } from '../hooks/useAppSettings';

export interface CoatingUpdateResponse {
    success: boolean;
    data: any;
    message: string;
}

/**
 * Custom hook for updating coating data
 * Uses POST endpoint: /UpsertCoating
 */
const useUpdateCoating = () => {
    const { state, postApiData } = useApi();
    const { settings: appSettings } = useAppSettings();

    /**
     * Updates a coating via the UpsertCoating API endpoint
     * @param coatingData - Complete coating data
     * @returns Promise<CoatingUpdateResponse>
     */
    const updateCoating = useCallback(
        async (coatingData: any): Promise<CoatingUpdateResponse> => {
            if (!coatingData || coatingData.id === undefined || coatingData.id === null) {
                throw new Error('Coating data with valid ID is required');
            }

            try {
                const apiUrl = appSettings?.api;
                if (!apiUrl) {
                    throw new Error('API URL not configured in app settings');
                }

                const endpoint = `${apiUrl}UpsertCoating`;

                // Convert camelCase to PascalCase for API
                const apiPayload = {
                    Id: coatingData.id,
                    Name: coatingData.name,
                    Type: coatingData.type,
                    IsActive: coatingData.isActive,
                    Manufacturer: coatingData.manufacturerId ? { Id: coatingData.manufacturerId } : null,
                    LastUpdatedUtc: new Date().toISOString(),
                };

                const response = await postApiData({
                    endpoint,
                    data: apiPayload,
                    config: { params: { releaseId: appSettings?.releaseId || '' } },
                });

                return {
                    success: true,
                    data: response,
                    message: 'Coating updated successfully',
                };
            } catch (error: any) {
                console.error('Failed to update coating:', error);

                const errorMessage = error.response?.data?.message || error.response?.statusText || error.message || 'Failed to update coating';

                throw new Error(errorMessage);
            }
        },
        [appSettings, postApiData]
    );

    return {
        state,
        updateCoating,
        isLoading: state.isLoading,
        error: state.error,
        response: state.result as CoatingUpdateResponse | null,
    };
};

export default useUpdateCoating;
